import { useState } from "react";
import { CheckCircle2, ClipboardCheck, CreditCard, GraduationCap, Laptop, Send, XCircle } from "lucide-react";
import { membershipApi } from "../api";
import { MEMBERSHIP_PERMISSIONS } from "../permissions";
import type { MemberCompany, MemberFeeRecord, MemberIctAccess, MembershipFeeType } from "../types";
import { currentFYDates, toFinancialYear } from "../utils/financialYear";
import { MembershipAccess } from "./MembershipAccess";

export function MembershipApprovalPanel({
  member,
  fees,
  ictAccess,
  feeTypes,
  onReload,
}: {
  member: MemberCompany;
  fees: MemberFeeRecord[];
  ictAccess?: MemberIctAccess;
  feeTypes: MembershipFeeType[];
  onReload: () => void;
}) {
  const [notes, setNotes] = useState("");
  const { from: defaultFrom, to: defaultTo } = currentFYDates();
  const [financeForm, setFinanceForm] = useState({
    feeTypeId: "",
    feeFromDate: defaultFrom,
    feeToDate: defaultTo,
    amountPaid: "",
    paymentReference: "",
    paymentDate: "",
    financeNotes: "",
  });
  const [ictForm, setIctForm] = useState({
    username: ictAccess?.username ?? "",
    portalAccountCreated: ictAccess?.portalAccountCreated ?? false,
    auctionAccessCreated: ictAccess?.auctionAccessCreated ?? false,
    ictNotes: ictAccess?.ictNotes ?? "",
  });

  const submitToBoard = async () => {
    await membershipApi.submitToBoard(member.id, notes);
    setNotes("");
    onReload();
  };

  const boardDecision = async (approved: boolean) => {
    await membershipApi.updateBoardDecision(member.id, { approved, notes });
    setNotes("");
    onReload();
  };

  const confirmPayment = async () => {
    const existingFee = fees[0];

    if (existingFee) {
      await membershipApi.updateFeeRecord(existingFee.id, {
        paymentStatus: "PAID",
        amountPaid: Number(financeForm.amountPaid || existingFee.amount || 0),
        paymentReference: financeForm.paymentReference,
        paymentDate: financeForm.paymentDate,
        financeNotes: financeForm.financeNotes,
      });
    } else {
      if (!financeForm.feeTypeId) {
        alert("Please select a fee type.");
        return;
      }

      const derivedFY = financeForm.feeFromDate
        ? toFinancialYear(new Date(financeForm.feeFromDate))
        : toFinancialYear(new Date());
      await membershipApi.createFeeRecord(member.id, {
        feeTypeId: Number(financeForm.feeTypeId),
        financialYear: derivedFY,
        dueDate: financeForm.feeToDate || undefined,
        paymentStatus: "PAID",
        amountPaid: Number(financeForm.amountPaid || 0),
        paymentReference: financeForm.paymentReference,
        paymentDate: financeForm.paymentDate,
        financeNotes: financeForm.financeNotes,
      } as Partial<MemberFeeRecord>);
    }

    onReload();
  };

  const updateIct = async () => {
    await membershipApi.updateIctAccess(member.id, ictForm);
    onReload();
  };

  const completeTraining = async () => {
    await membershipApi.updateTraining(member.id, { completed: true, notes });
    setNotes("");
    onReload();
  };

  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <h2 className="text-lg font-black text-slate-900">Approval Actions</h2>

      <div className="mt-5 grid gap-4">
        <textarea
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          placeholder="Notes / decision remarks..."
          className="min-h-24 rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
        />

        <MembershipAccess permissions={[MEMBERSHIP_PERMISSIONS.TRADE_UPDATE]}>
          <ActionBlock
            icon={Send}
            title="Trade / Membership Officer"
          >
            <button
              type="button"
              onClick={submitToBoard}
              className="rounded-2xl bg-amber-600 px-4 py-3 text-sm font-black text-white hover:bg-amber-700"
            >
              Submit to Board
            </button>
          </ActionBlock>
        </MembershipAccess>

        <MembershipAccess permissions={[MEMBERSHIP_PERMISSIONS.BOARD_UPDATE, MEMBERSHIP_PERMISSIONS.APPROVE]}>
          <ActionBlock
            icon={ClipboardCheck}
            title="Board Decision"
          >
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => boardDecision(true)}
                className="inline-flex items-center gap-2 rounded-2xl bg-emerald-700 px-4 py-3 text-sm font-black text-white hover:bg-emerald-800"
              >
                <CheckCircle2 className="h-4 w-4" />
                Approve
              </button>

              <button
                type="button"
                onClick={() => boardDecision(false)}
                className="inline-flex items-center gap-2 rounded-2xl bg-red-700 px-4 py-3 text-sm font-black text-white hover:bg-red-800"
              >
                <XCircle className="h-4 w-4" />
                Reject
              </button>
            </div>
          </ActionBlock>
        </MembershipAccess>

        <MembershipAccess permissions={[MEMBERSHIP_PERMISSIONS.FINANCE_UPDATE]}>
          <ActionBlock
            icon={CreditCard}
            title="Finance Payment Confirmation"
          >
            <div className="grid gap-3 md:grid-cols-2">
              {!fees.length && (
                <select
                  value={financeForm.feeTypeId}
                  onChange={(event) =>
                    setFinanceForm((prev) => ({ ...prev, feeTypeId: event.target.value }))
                  }
                  className="rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                >
                  <option value="">Select Fee Type</option>
                  {feeTypes.map((fee) => (
                    <option key={fee.id} value={fee.id}>
                      {fee.name}
                    </option>
                  ))}
                </select>
              )}

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Period From</label>
                <input
                  type="date"
                  value={financeForm.feeFromDate}
                  onChange={(event) =>
                    setFinanceForm((prev) => ({ ...prev, feeFromDate: event.target.value }))
                  }
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Period To</label>
                <input
                  type="date"
                  value={financeForm.feeToDate}
                  onChange={(event) =>
                    setFinanceForm((prev) => ({ ...prev, feeToDate: event.target.value }))
                  }
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                />
              </div>

              {financeForm.feeFromDate && (
                <div className="md:col-span-2 flex items-center gap-2">
                  <span className="text-xs text-slate-400">Financial year:</span>
                  <span className="rounded-full bg-emerald-50 border border-emerald-200 px-3 py-0.5 text-xs font-black text-emerald-700">
                    {toFinancialYear(new Date(financeForm.feeFromDate))}
                  </span>
                </div>
              )}

              <input
                value={financeForm.amountPaid}
                onChange={(event) =>
                  setFinanceForm((prev) => ({ ...prev, amountPaid: event.target.value }))
                }
                placeholder="Amount paid"
                className="rounded-2xl border border-slate-200 px-4 py-3 text-sm"
              />

              <input
                value={financeForm.paymentReference}
                onChange={(event) =>
                  setFinanceForm((prev) => ({ ...prev, paymentReference: event.target.value }))
                }
                placeholder="Payment reference"
                className="rounded-2xl border border-slate-200 px-4 py-3 text-sm"
              />

              <input
                type="date"
                value={financeForm.paymentDate}
                onChange={(event) =>
                  setFinanceForm((prev) => ({ ...prev, paymentDate: event.target.value }))
                }
                className="rounded-2xl border border-slate-200 px-4 py-3 text-sm"
              />
            </div>

            <button
              type="button"
              onClick={confirmPayment}
              className="mt-3 rounded-2xl bg-emerald-700 px-4 py-3 text-sm font-black text-white hover:bg-emerald-800"
            >
              Confirm Payment
            </button>
          </ActionBlock>
        </MembershipAccess>

        <MembershipAccess permissions={[MEMBERSHIP_PERMISSIONS.ICT_UPDATE]}>
          <ActionBlock
            icon={Laptop}
            title="ICT Account Creation"
          >
            <div className="grid gap-3">
              <input
                value={ictForm.username}
                onChange={(event) =>
                  setIctForm((prev) => ({ ...prev, username: event.target.value }))
                }
                placeholder="System username"
                className="rounded-2xl border border-slate-200 px-4 py-3 text-sm"
              />

              <label className="flex items-center gap-3 text-sm font-bold text-slate-700">
                <input
                  type="checkbox"
                  checked={ictForm.portalAccountCreated}
                  onChange={(event) =>
                    setIctForm((prev) => ({
                      ...prev,
                      portalAccountCreated: event.target.checked,
                    }))
                  }
                />
                Portal account created
              </label>

              <label className="flex items-center gap-3 text-sm font-bold text-slate-700">
                <input
                  type="checkbox"
                  checked={ictForm.auctionAccessCreated}
                  onChange={(event) =>
                    setIctForm((prev) => ({
                      ...prev,
                      auctionAccessCreated: event.target.checked,
                    }))
                  }
                />
                Auction access created
              </label>

              <button
                type="button"
                onClick={updateIct}
                className="rounded-2xl bg-blue-700 px-4 py-3 text-sm font-black text-white hover:bg-blue-800"
              >
                Save ICT Setup
              </button>
            </div>
          </ActionBlock>
        </MembershipAccess>

        <MembershipAccess permissions={[MEMBERSHIP_PERMISSIONS.TRADE_UPDATE]}>
          <ActionBlock
            icon={GraduationCap}
            title="Training Completion"
          >
            <button
              type="button"
              onClick={completeTraining}
              className="rounded-2xl bg-emerald-700 px-4 py-3 text-sm font-black text-white hover:bg-emerald-800"
            >
              Mark Training Complete
            </button>
          </ActionBlock>
        </MembershipAccess>
      </div>
    </section>
  );
}

function ActionBlock({
  icon: Icon,
  title,
  description,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
      <div className="mb-3 flex gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-emerald-700">
          <Icon className="h-5 w-5" />
        </div>

        <div>
          <h3 className="text-sm font-black text-slate-900">{title}</h3>
          {description && <p className="text-sm text-slate-500">{description}</p>}
        </div>
      </div>

      {children}
    </div>
  );
}